import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import FeedCard from "@/components/feed/FeedCard";
import { Lock, Sparkles, Loader2 } from "lucide-react";

const LAST_SEEN_KEY = "stx_exclusive_last_seen";

/**
 * StxExclusivePage · P6 Task4 — stilltouchedX „Exklusiv".
 * Zeigt NUR gekauften STX-Exklusiv-Content + aktive STX-Abos (inkl. neuer
 * Beiträge der abonnierten Creator). Leer-Hinweis, wenn nichts vorhanden.
 * STX-Theme (Light/Dark via --stx-*). Autoren strikt über stx_profiles.
 */
export default function StxExclusivePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [abo, setAbo] = useState({ subscribed_creators: [], aggregated_posts: [], has_subs: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const results = await Promise.allSettled([
          api.get("/me/purchases/stx"),
          api.get("/api/stx/exclusive"),
        ]);
        if (!alive) return;
        if (results[0].status === "fulfilled") setPurchases(results[0].value.data?.purchases || []);
        if (results[1].status === "fulfilled") setAbo(results[1].value.data || {});
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    // Badge zurücksetzen: Zeitpunkt des letzten Besuchs merken
    try { localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString()); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("stx-exclusive-seen"));
    return () => { alive = false; };
  }, []);

  const isEmpty = !loading && purchases.length === 0 && !abo.has_subs && (abo.aggregated_posts || []).length === 0;

  return (
    <div data-testid="stx-exclusive-page" className="bg-stx-bg text-stx-text min-h-full pb-16">
      <div className="max-w-2xl mx-auto px-4 pt-5">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Sparkles size={18} className="text-stx-accent" /> {t("stx_exclusive.title", { defaultValue: "Exklusiv" })}
        </h1>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-stx-accent" size={24} /></div>
        ) : isEmpty ? (
          <div data-testid="stx-exclusive-empty" className="mt-8 text-center rounded-xl border border-stx-border bg-stx-panel p-6">
            <Lock size={28} className="mx-auto text-stx-text-muted" />
            <p className="mt-3 text-sm font-semibold">{t("stx_exclusive.empty_title", { defaultValue: "Du hast noch keine Abos oder exklusiven Content" })}</p>
            <p className="mt-1 text-xs text-stx-text-muted max-w-sm mx-auto">
              {t("stx_exclusive.empty_body", { defaultValue: "Sobald du ein Creator-Abo hast, siehst du hier bestehende und neue Beiträge deines Abos sowie von dir erworbene exklusive Beiträge." })}
            </p>
          </div>
        ) : (
          <div className="mt-5 space-y-6">
            {/* Abonnierte STX-Creator */}
            {abo.subscribed_creators?.length > 0 && (
              <section data-testid="stx-exclusive-creators">
                <p className="text-xs font-medium text-stx-text-muted uppercase tracking-wider mb-2">{t("stx_exclusive.your_abos", { defaultValue: "Deine Abos" })}</p>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {abo.subscribed_creators.map((c) => (
                    <button key={c.stx_public_id || c.handle} onClick={() => c.handle && navigate(`/user/${c.handle}/stx`)}
                      className="flex flex-col items-center shrink-0">
                      <span className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-stx-accent bg-stx-panel-2 flex items-center justify-center">
                        {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-bold text-stx-text-muted">{(c.display_name || "?")[0]}</span>}
                      </span>
                      <span className="mt-1 text-[10px] text-stx-text-muted max-w-[64px] truncate">{c.display_name}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Gekaufte STX-Inhalte */}
            {purchases.length > 0 && (
              <section data-testid="stx-exclusive-purchases">
                <p className="text-xs font-medium text-stx-text-muted uppercase tracking-wider mb-2">{t("stx_exclusive.purchases_title", { defaultValue: "Gekaufte Inhalte" })}</p>
                <div className="grid grid-cols-2 gap-3">
                  {purchases.map((p) => (
                    <button key={p.entitlement_id || p.resource_id} data-testid={`stx-exclusive-purchase-${p.entitlement_id || p.resource_id}`}
                      onClick={() => navigate("/profile/purchases")}
                      className="text-left rounded-lg overflow-hidden border border-stx-border bg-stx-panel hover:border-stx-accent transition-colors">
                      {(p.asset?.thumbnail_url || p.asset?.media_url) ? (
                        <img src={p.asset.thumbnail_url || p.asset.media_url} alt="" className="w-full aspect-square object-cover" />
                      ) : (
                        <div className="w-full aspect-square bg-stx-panel-2 flex items-center justify-center"><Lock size={18} className="text-stx-text-muted" /></div>
                      )}
                      <div className="p-2"><p className="text-xs truncate">{p.asset?.title || p.stx_order?.listing_title || t("stx_exclusive.item", { defaultValue: "Inhalt" })}</p></div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Aggregierte Abo-Beiträge */}
            {abo.aggregated_posts?.length > 0 && (
              <section data-testid="stx-exclusive-posts">
                <p className="text-xs font-medium text-stx-text-muted uppercase tracking-wider mb-2">{t("stx_exclusive.abo_posts", { defaultValue: "Neue Beiträge deiner Abos" })}</p>
                <div>{abo.aggregated_posts.map((p) => <FeedCard key={p.post_id} post={p} />)}</div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
